import { useState } from 'react'

export const KnowledgeBase = () => {
  const [activeSection, setActiveSection] = useState('overview')
  const [searchTerm, setSearchTerm] = useState('')

  const sections = [
    { id: 'overview', title: 'Platform Overview', icon: '🏠' },
    { id: 'getting-started', title: 'Getting Started', icon: '🚀' },
    { id: 'modules', title: 'Analysis Modules', icon: '�' },
    { id: 'tips', title: 'Tips & Best Practices', icon: '💡' },
    { id: 'glossary', title: 'Investment Glossary', icon: '📚' }
  ]

  const renderContent = () => {
    switch (activeSection) {
      case 'overview':
        return (
          <div className="space-y-6">
            <div className="bg-blue-50 p-6 rounded-lg border border-blue-200">
              <h3 className="text-xl font-semibold text-blue-900 mb-3">Welcome to StratOS</h3>
              <p className="text-blue-800 leading-relaxed">
                StratOS is your intelligent investment analysis platform that helps you make smarter investment decisions. 
                Think of it as having a team of expert analysts working 24/7 to evaluate investment opportunities for you.
              </p>
            </div>

            <div 
              className="grid grid-cols-1 xl:grid-cols-2 gap-6"
              style={{ 
                width: '100%', 
                maxWidth: 'none',
                overflow: 'hidden'
              }}
            >
              <div className="bg-white p-6 rounded-lg border border-gray-200">
                <h4 className="font-semibold text-gray-900 mb-3">🎯 What Does It Do?</h4>
                <ul className="space-y-2 text-gray-700">
                  <li>• Analyzes stocks, cryptocurrencies, and emerging technologies</li>
                  <li>• Provides investment recommendations with confidence scores</li>
                  <li>• Identifies market opportunities and risks</li>
                  <li>• Tracks technology trends and adoption patterns</li>
                  <li>• Monitors for potential company takeovers</li>
                </ul>
              </div>

              <div className="bg-white p-6 rounded-lg border border-gray-200">
                <h4 className="font-semibold text-gray-900 mb-3">👥 Who Is It For?</h4>
                <ul className="space-y-2 text-gray-700">
                  <li>• Individual investors looking to improve their returns</li>
                  <li>• Financial advisors serving clients</li>
                  <li>• Investment firms and hedge funds</li>
                  <li>• Anyone interested in technology investing</li>
                  <li>• People who want data-driven investment insights</li>
                </ul>
              </div>
            </div>

            <div className="bg-yellow-50 p-6 rounded-lg border border-yellow-200">
              <h4 className="font-semibold text-yellow-900 mb-4">🚀 Key Benefits</h4>
              <div 
                className="grid grid-cols-1 lg:grid-cols-3 gap-4 text-yellow-800"
                style={{ 
                  width: '100%', 
                  maxWidth: 'none',
                  overflow: 'hidden'
                }}
              >
                <div className="bg-yellow-100 p-4 rounded-lg">
                  <strong>Save Time:</strong> Get instant analysis instead of spending hours researching
                </div>
                <div className="bg-yellow-100 p-4 rounded-lg">
                  <strong>Reduce Risk:</strong> Identify potential problems before they happen
                </div>
                <div className="bg-yellow-100 p-4 rounded-lg">
                  <strong>Find Opportunities:</strong> Discover investments you might have missed
                </div>
              </div>
            </div>
          </div>
        )

      case 'getting-started':
        return (
          <div className="space-y-6">
            <div className="bg-green-50 p-6 rounded-lg border border-green-200">
              <h3 className="text-xl font-semibold text-green-900 mb-3">Your First Steps</h3>
              <p className="text-green-800 leading-relaxed">
                Getting started with StratOS is simple. Follow these steps to begin analyzing investments like a pro.
              </p>
            </div>

            <div className="space-y-6">
              {[
                {
                  step: "1",
                  title: "Start with the Dashboard",
                  description: "The Dashboard shows you an overview of market activity and recent analysis. It's your home base.",
                  tip: "What you'll see: Portfolio value, risk scores, recent asset evaluations, and active market alerts."
                },
                {
                  step: "2", 
                  title: "Try Your First Analysis",
                  description: "Go to 'Asset Evaluation' and enter a stock symbol you're interested in (like AAPL for Apple).",
                  tip: "Start with well-known companies to get familiar with how the analysis works."
                },
                {
                  step: "3",
                  title: "Understand the Results", 
                  description: "Each analysis gives you scores from 0-100. Higher scores mean better investment potential.",
                  tip: "Good scores: 70+ is generally positive, 80+ is strong, 90+ is excellent."
                },
                {
                  step: "4",
                  title: "Explore Other Modules",
                  description: "Once comfortable with basic analysis, try the other tools for complete investment insights.",
                  tip: "Use multiple tools together for the best results. No single analysis tells the whole story."
                }
              ].map((item, index) => (
                <div key={index} className="bg-white p-6 rounded-lg border border-gray-200">
                  <div className="flex items-start gap-4">
                    <div className="bg-blue-100 text-blue-600 w-8 h-8 rounded-full flex items-center justify-center font-bold">{item.step}</div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">{item.title}</h4>
                      <p className="text-gray-700 mb-3">{item.description}</p>
                      <div className="bg-gray-50 p-3 rounded">
                        <strong>Tip:</strong> {item.tip}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )

      case 'modules':
        return (
          <div className="space-y-6">
            <div className="bg-purple-50 p-6 rounded-lg border border-purple-200">
              <h3 className="text-xl font-semibold text-purple-900 mb-3">Understanding the Analysis Modules</h3>
              <p className="text-purple-800 leading-relaxed">
                Each module provides different insights. Here's what they do and when to use them.
              </p>
            </div>

            <div className="space-y-6">
              {[
                {
                  name: "Asset Evaluation Engine",
                  icon: "📊",
                  description: "Analyzes individual investments and provides overall scores",
                  whatItDoes: "Evaluates technical indicators, fundamental metrics, sentiment, and risk factors",
                  whenToUse: "Use this first for any investment you're considering",
                  keyScores: ["Overall Score (0-100)", "Technical Analysis", "Fundamental Health", "Market Sentiment", "Risk Assessment"]
                },
                {
                  name: "Conviction & Catalyst Scoring",
                  icon: "🎯", 
                  description: "Measures investment confidence by analyzing upcoming events",
                  whatItDoes: "Identifies catalysts (earnings, product launches) and their probability of success",
                  whenToUse: "When you want to know what might drive the stock price up or down",
                  keyScores: ["Conviction Level", "Catalyst Probability", "Impact Assessment", "Timing Predictions", "Confidence Rating"]
                },
                {
                  name: "Quantum Learning Curve Model",
                  icon: "⚡",
                  description: "Predicts technology adoption and cost efficiency improvements",
                  whatItDoes: "Models how emerging technologies scale, become cheaper, and gain market adoption",
                  whenToUse: "For technology companies and emerging industries",
                  keyScores: ["Cost Decline Rate", "Learning Efficiency", "Adoption Speed", "Market Penetration", "Risk/Opportunity Factors"]
                },
                {
                  name: "Hostile Acquisition Scanner", 
                  icon: "🔍",
                  description: "Identifies companies vulnerable to takeover attempts",
                  whatItDoes: "Monitors trading patterns, insider activity, and structural weaknesses",
                  whenToUse: "To find potential takeover targets or assess acquisition risk",
                  keyScores: ["Risk Level (High/Medium/Low)", "Vulnerability Factors", "Defensive Measures", "Timeline Estimates", "Confidence Level"]
                }
              ].map((module, index) => (
                <div key={index} className="bg-white p-6 rounded-lg border border-gray-200">
                  <div className="flex items-start gap-4 mb-4">
                    <div className="text-3xl">{module.icon}</div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">{module.name}</h4>
                      <p className="text-gray-700 mb-3">{module.description}</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 text-sm"
                    style={{ 
                      width: '100%', 
                      maxWidth: 'none',
                      overflow: 'hidden'
                    }}
                  >
                    <div className="bg-blue-50 p-3 rounded">
                      <strong className="text-blue-800">What it does:</strong>
                      <p className="text-blue-700 mt-1">{module.whatItDoes}</p>
                    </div>
                    <div className="bg-green-50 p-3 rounded">
                      <strong className="text-green-800">When to use:</strong>
                      <p className="text-green-700 mt-1">{module.whenToUse}</p>
                    </div>
                    <div className="bg-yellow-50 p-3 rounded">
                      <strong className="text-yellow-800">Key outputs:</strong>
                      <ul className="text-yellow-700 mt-1 space-y-1">
                        {module.keyScores.map((score, i) => (
                          <li key={i}>• {score}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )

      case 'tips':
        return (
          <div className="space-y-6">
            <div className="bg-green-50 p-6 rounded-lg border border-green-200">
              <h3 className="text-xl font-semibold text-green-900 mb-3">Maximize Your StratOS Experience</h3>
              <p className="text-green-800 leading-relaxed">
                These proven strategies will help you get the most accurate analysis and make better investment decisions.
              </p>
            </div>

            <div className="space-y-6">
              <div className="bg-white p-6 rounded-lg border border-gray-200">
                <h4 className="font-semibold text-gray-900 mb-4">🎯 Best Practices</h4>
                <div className="space-y-4">
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <h5 className="font-medium text-blue-800 mb-2">Use Multiple Timeframes</h5>
                    <p className="text-blue-700 text-sm">Run the same analysis with different time periods:</p>
                    <ul className="text-blue-600 text-sm mt-2 space-y-1">
                      <li>• 1-7 days: Short-term trading opportunities</li>
                      <li>• 30-90 days: Medium-term trends</li>
                      <li>• 1 year: Long-term investment perspective</li>
                    </ul>
                  </div>
                  
                  <div className="bg-purple-50 p-4 rounded-lg">
                    <h5 className="font-medium text-purple-800 mb-2">Compare Similar Assets</h5>
                    <p className="text-purple-700 text-sm">
                      Don't analyze just one stock. Compare 3-5 similar companies to find the best opportunity.
                    </p>
                  </div>

                  <div className="bg-orange-50 p-4 rounded-lg">
                    <h5 className="font-medium text-orange-800 mb-2">Use All Modules Together</h5>
                    <p className="text-orange-700 text-sm">For complete analysis: Start with Asset Evaluation → Check Conviction Scoring → Use Quantum Learning (for tech) → Run Hostile Acquisition scan</p>
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-lg border border-gray-200">
                <h4 className="font-semibold text-gray-900 mb-4">⚠️ Common Mistakes to Avoid</h4>
                <div className="space-y-3">
                  <div className="border-l-4 border-red-400 bg-red-50 p-4">
                    <strong className="text-red-800">Don't rely on just one analysis</strong>
                    <p className="text-red-700 text-sm mt-1">Always get multiple data points. One good score doesn't guarantee success.</p>
                  </div>
                  <div className="border-l-4 border-yellow-400 bg-yellow-50 p-4">
                    <strong className="text-yellow-800">Don't ignore confidence levels</strong>
                    <p className="text-yellow-700 text-sm mt-1">A score of 85 with 95% confidence is much better than 90 with 60% confidence.</p>
                  </div>
                  <div className="border-l-4 border-blue-400 bg-blue-50 p-4">
                    <strong className="text-blue-800">Don't forget market conditions</strong>
                    <p className="text-blue-700 text-sm mt-1">Great analysis in a bear market might still lead to losses. Consider overall market health.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )

      case 'glossary':
        return (
          <div className="space-y-6">
            <div className="bg-indigo-50 p-6 rounded-lg border border-indigo-200">
              <h3 className="text-xl font-semibold text-indigo-900 mb-3">Key Terms & Definitions</h3>
              <p className="text-indigo-800 leading-relaxed">
                Understanding these terms will help you get the most out of your StratOS analysis.
              </p>
            </div>

            <div className="space-y-6">
              <div className="bg-white p-6 rounded-lg border border-gray-200">
                <h4 className="font-semibold text-gray-900 mb-4">📊 Analysis Terms</h4>
                <div 
                  className="grid grid-cols-1 lg:grid-cols-2 gap-4 text-sm"
                  style={{ 
                    width: '100%', 
                    maxWidth: 'none',
                    overflow: 'hidden'
                  }}
                >
                  {[
                    { term: "Technical Analysis", definition: "Studying price charts and patterns to predict future movements" },
                    { term: "Fundamental Analysis", definition: "Evaluating company's financial health and business prospects" },
                    { term: "Sentiment Analysis", definition: "Measuring investor emotions and market mood toward a stock" },
                    { term: "Volatility", definition: "How much a stock's price goes up and down (high volatility = more risk)" },
                    { term: "Risk Score", definition: "A number showing how likely you are to lose money on an investment" },
                    { term: "Confidence Level", definition: "How sure the system is about its analysis results" },
                    { term: "Market Cap", definition: "The total value of all a company's shares (small, mid, large, mega)" },
                    { term: "Beta", definition: "How much a stock moves compared to the overall market" }
                  ].map((item, index) => (
                    <div key={index} className="border-l-4 border-blue-400 pl-3">
                      <strong>{item.term}:</strong> {item.definition}
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white p-6 rounded-lg border border-gray-200">
                <h4 className="font-semibold text-gray-900 mb-4">🎯 Investment Strategy Terms</h4>
                <div 
                  className="grid grid-cols-1 lg:grid-cols-2 gap-4 text-sm"
                  style={{ 
                    width: '100%', 
                    maxWidth: 'none',
                    overflow: 'hidden'
                  }}
                >
                  {[
                    { term: "Bull Market", definition: "When stock prices are generally rising and confidence is high", color: "green" },
                    { term: "Bear Market", definition: "When stock prices are falling and investors are pessimistic", color: "red" },
                    { term: "Diversification", definition: "Spreading investments across different assets to reduce risk", color: "blue" },
                    { term: "Portfolio", definition: "Your collection of all investments (stocks, bonds, etc.)", color: "purple" },
                    { term: "Asset Class", definition: "Categories of investments (stocks, bonds, real estate, crypto)", color: "yellow" },
                    { term: "Hedge", definition: "An investment made to offset potential losses in another investment", color: "orange" }
                  ].map((item, index) => (
                    <div key={index} className={`bg-${item.color}-50 p-3 rounded border border-${item.color}-200`}>
                      <strong className={`text-${item.color}-800`}>{item.term}:</strong>
                      <span className={`text-${item.color}-700`}> {item.definition}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )

      default:
        return <div>Content not found</div>
    }
  }

  const filteredSections = sections.filter(section =>
    section.title.toLowerCase().includes(searchTerm.toLowerCase())
  )

  return (
    <div 
      className="w-full max-w-none space-y-6" 
      style={{ 
        width: '100%', 
        maxWidth: 'none', 
        minWidth: '0',
        flex: '1' 
      }}
    >
      {/* Header */}
      <div className="text-center">
        <h1 className="text-3xl font-bold text-gray-900 mb-3">StratOS Knowledge Base</h1>
        <p className="text-gray-600">Everything you need to know about using the StratOS investment intelligence platform</p>
      </div>

      {/* Search */}
      <div className="flex justify-center">
        <div className="relative w-full max-w-2xl">
          <input
            type="text"
            placeholder="Search the knowledge base..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          <div className="absolute left-4 top-3.5 text-gray-400 text-lg">🔍</div>
        </div>
      </div>

      {/* Main Content */}
      <div 
        className="flex flex-col lg:flex-row gap-6" 
        style={{ 
          width: '100%', 
          maxWidth: 'none'
        }}
      >
        {/* Sidebar */}
        <div className="lg:w-80 flex-shrink-0">
          <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
            <h3 className="font-semibold text-gray-900 mb-4">Topics</h3>
              <nav className="space-y-2">
                {filteredSections.map((section) => (
                  <button
                    key={section.id}
                    onClick={() => setActiveSection(section.id)}
                    className={`w-full text-left px-4 py-3 rounded-lg text-sm transition-colors ${
                      activeSection === section.id
                        ? 'bg-blue-100 text-blue-700 font-medium'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <span className="mr-3">{section.icon}</span>
                    {section.title}
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Content */}
          <div 
            className="flex-1 min-w-0" 
            style={{ 
              width: '100%', 
              maxWidth: 'none'
            }}
          >
            <div 
              className="bg-white rounded-lg shadow border border-gray-200 p-8"
              style={{ 
                width: '100%', 
                maxWidth: 'none'
              }}
            >
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                {sections.find(s => s.id === activeSection)?.title}
              </h2>
              <div 
                className="prose prose-lg max-w-none"
                style={{ 
                  width: '100%', 
                  maxWidth: 'none'
                }}
              >
                {renderContent()}
              </div>
            </div>
          </div>
        </div>
    </div>
  )
}